import React, { useCallback, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import Navbar, { NAVBAR_HEIGHT } from '../../../components/Navbar';
import {
  ACTIVITY_PAGE_TYPE,
  ActivityLayout,
} from '../../../components/Activity';
import { ACTIVITY_TYPE, ActivityProvider } from '../../../contexts/activity';
import { ROUTES } from '../../../routing';
import { CmsModuleDefinitionSchema } from '../../../schemas/content-module';
import type { CmsModuleDefinition } from '../../../schemas/content-module';
import { cmsModuleActivityToActivityResponse } from '../../../lib/cms-module-to-dashboard';
import type { ActivityPayload } from '../../../hydra';
import { previewValidateAnswer } from '../../../api/admin';
import useAuth from '../../../hooks/use-auth';
import AIChat from '../../../components/AIChat';
import { buildCmsPreviewChatContext } from '../../../lib/cms-preview-chat-context';
import { AIWorkflowProvider } from '../../../contexts/ai-workflow';

const PREVIEW_STORAGE_KEY = 'enki:preview:module';
const PREVIEW_MAX_AGE_MS = 60 * 60 * 1000;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const StyledAIChat = styled(AIChat)`
  height: calc(100vh - 2 * ${NAVBAR_HEIGHT}px - 32px);
  bottom: calc(${NAVBAR_HEIGHT}px + 16px);
  box-shadow: 0px 0px 16px rgba(0, 0, 0, 0.2);
`;

const FadeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  animation: ${fadeIn} 0.4s ease-in-out forwards;
  height: 100vh;
`;

const StyledNavbar = styled(Navbar)`
  position: static;
`;

const ErrorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: calc(100vh - ${NAVBAR_HEIGHT}px);
  gap: 16px;
  color: ${({ theme }) => theme.colors.textRegular};
  font-size: 18px;
`;

export default function CMSPreviewModuleActivity() {
  const { slug, activitySlug } =
    useParams<{
      slug: string;
      activitySlug: string;
    }>();
  const { getTokenSilently } = useAuth();
  const [previewData, setPreviewData] =
    React.useState<ActivityPayload | null>(null);
  const [cmsDefinition, setCmsDefinition] =
    React.useState<CmsModuleDefinition | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const previewDataRef = useRef(previewData);
  React.useEffect(() => {
    previewDataRef.current = previewData;
  }, [previewData]);

  React.useEffect(() => {
    try {
      const raw = localStorage.getItem(PREVIEW_STORAGE_KEY);
      if (!raw) {
        setError(
          'No preview data found. Open the preview from the editor page.'
        );
        return;
      }

      const parsed = JSON.parse(raw);

      if (
        parsed._createdAt &&
        Date.now() - parsed._createdAt > PREVIEW_MAX_AGE_MS
      ) {
        localStorage.removeItem(PREVIEW_STORAGE_KEY);
        setError(
          'Preview data has expired. Open a new preview from the editor.'
        );
        return;
      }

      const result = CmsModuleDefinitionSchema.safeParse(parsed);
      if (!result.success) {
        setError(
          `Invalid preview data: ${result.error.issues
            .map((i) => i.message)
            .join(', ')}`
        );
        return;
      }

      setCmsDefinition(result.data);

      const activityResponse = cmsModuleActivityToActivityResponse(
        result.data,
        activitySlug ?? ''
      );

      if (!activityResponse) {
        setError(
          `Activity "${activitySlug}" not found in the module definition.`
        );
        return;
      }

      setPreviewData(activityResponse);
    } catch (e) {
      setError(`Failed to load preview data: ${(e as Error).message}`);
    }
  }, [activitySlug]);

  const activityState = {
    loading: false,
    error: null,
    value: previewData,
    retry: () => {},
  };

  const getActivity = useCallback(async (): Promise<ActivityPayload> => {
    const data = previewDataRef.current;
    if (!data) throw new Error('Preview data is not loaded');
    return data;
  }, []);

  // The preview never persists: submissions are discarded.
  const saveSubmission = useCallback(async () => {}, []);

  const goToNextActivity = useCallback(async () => {
    if (!previewData?.next_activity_slug || !slug) return;
    window.location.href = ROUTES.ADMIN.CMS_MODULE_PREVIEW_ACTIVITY.getPath({
      slug,
      activitySlug: previewData.next_activity_slug,
    });
  }, [previewData, slug]);

  const createAIReview = useCallback(async () => {}, []);

  const submitAnswer = useCallback(
    async ({
      questionSlug,
      value,
    }: {
      questionSlug: string;
      value: string;
    }) => {
      if (!cmsDefinition || !activitySlug) return;

      const activity = cmsDefinition.weeks
        .flatMap((w) => w.missions)
        .flatMap((m) => m.activities)
        .find((a) => a.slug === activitySlug);
      if (!activity) return;

      const cmsQuestion = activity.questions.find(
        (q) => q.slug === questionSlug
      );
      if (!cmsQuestion) return;

      const isAIQuestion =
        cmsQuestion.type === 'LLM_PLAYGROUND' ||
        cmsQuestion.type === 'FREE_TEXT_AI_VERIFIED';
      if (!isAIQuestion) return;

      try {
        const token = await getTokenSilently();
        const result = await previewValidateAnswer(token, {
          questionText: cmsQuestion.text,
          questionNotes: cmsQuestion.notes ?? '',
          questionType: cmsQuestion.type,
          modelAnswer: cmsQuestion.modelAnswer ?? null,
          userAnswer: value,
        });

        setPreviewData((prev) => {
          if (!prev) return prev;
          const nowIso = new Date().toISOString();
          const base = prev.latest_submission ?? {
            id: -1,
            updated_at: nowIso,
            created_at: nowIso,
            difficulty_rating: null,
            feedback: null,
            seconds_spent: null,
            activity_session_id: -1,
            ai_review: null,
            answer_states: {},
            mentor_reviews: null,
          };
          const currentStates = base.answer_states ?? {};
          const updated: ActivityPayload = {
            ...prev,
            latest_submission: {
              ...base,
              answer_states: {
                ...currentStates,
                [questionSlug]: {
                  ...(currentStates[questionSlug] ?? {
                    value,
                    is_correct: null,
                    is_submitted: false,
                    submitted_at: null,
                  }),
                  value,
                  is_submitted: true,
                  submitted_at: new Date(),
                  result: result.result,
                  review: result.review,
                  is_correct: result.isCorrect,
                  does_satisfy_all_requirements:
                    result.doesSatisfyAllRequirements,
                },
              },
            },
          };
          previewDataRef.current = updated;
          return updated;
        });
      } catch (err) {
        console.error('Preview validation failed:', err);
      }
    },
    [cmsDefinition, activitySlug, getTokenSilently]
  );

  const modulePreviewUrl = ROUTES.ADMIN.CMS_MODULE_PREVIEW.getPath({
    slug: slug ?? '',
  });

  const breadCrumbs = [
    { label: 'Admin Dashboard', url: ROUTES.ADMIN.HOME.getPath() },
    { label: 'Modules', url: ROUTES.ADMIN.CMS_MODULES.getPath() },
    { label: cmsDefinition?.title ?? 'Module', url: modulePreviewUrl },
    { label: previewData?.title ?? 'Activity', url: null },
  ];

  return (
    <AIWorkflowProvider>
      <FadeWrapper>
        <StyledNavbar isPreview breadCrumbs={breadCrumbs} />
        {error && <ErrorWrapper>{error}</ErrorWrapper>}
        {previewData && (
          <>
            <Helmet>
              <title>{previewData.title} — Preview</title>
            </Helmet>
            {/* Send the bounded prompt fields for every ordered question, not the
              complete CMS draft. The chat initializes this context once. */}
            <StyledAIChat
              contentId={null}
              missionId={null}
              activityId={null}
              projectId={null}
              discussionContext={'cms-preview-activity'}
              previewContext={buildCmsPreviewChatContext(previewData)}
              shouldShowChatIcon={false}
            />
            <ActivityProvider
              activity={previewData}
              type={ACTIVITY_TYPE.ACTIVITY}
            >
              <ActivityLayout
                type={ACTIVITY_PAGE_TYPE.PREVIEW}
                allowAIChatInPreview
                goToNextActivity={goToNextActivity}
                saveSubmission={saveSubmission}
                getActivity={getActivity}
                createAIReview={createAIReview}
                dashboardUrl={modulePreviewUrl}
                activityState={activityState}
                submitAnswer={submitAnswer}
              />
            </ActivityProvider>
          </>
        )}
      </FadeWrapper>
    </AIWorkflowProvider>
  );
}
